import { clamp } from 'asteroids-shared/helpers'

const createNoiseBuffer = (ctx, duration) => {
  const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    samples[i] = Math.random() * 2 - 1;
  }
  return buffer
}

export default class GameAudio {
  constructor() {
    this.AudioContextCtor = window.AudioContext || window.webkitAudioContext || null;
    this.ctx = null;
    this.master = null;
    this.thrustGain = null;
    this.thrustBodyFilter = null;
    this.thrustFormantA = null;
    this.thrustFormantB = null;
    this.thrustToneOsc = null;
    this.noiseBuffer = null;
    this.explosionBuffer = null;
    this.activeExplosions = 0;
    this.destroyed = false;
  }

  async resume() {
    if (!this.AudioContextCtor || this.destroyed) return;
    if (!this.ctx) this.setup();
    if (this.ctx?.state === 'suspended') {
      try { await this.ctx.resume(); } catch {}
    }
  }

  playTone({
    when,
    frequency,
    duration,
    type = 'triangle',
    peak = 0.028
  }) {
    if (!this.ctx || !this.master) return;

    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, when);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = frequency * 3.2;
    filter.Q.value = 0.8;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.linearRampToValueAtTime(peak, when + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);

    osc.addEventListener('ended', () => {
      osc.disconnect();
      filter.disconnect();
      gain.disconnect();
    }, { once: true });

    osc.start(when);
    osc.stop(when + duration + 0.02);
  }

  setup() {
    this.ctx = new this.AudioContextCtor();
    const compressor = this.ctx.createDynamicsCompressor();
    compressor.threshold.value = -20;
    compressor.knee.value = 10;
    compressor.ratio.value = 12;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.18;

    this.master = this.ctx.createGain();
    this.master.gain.value = 1.9;
    this.master.connect(compressor);
    compressor.connect(this.ctx.destination);

    this.noiseBuffer = createNoiseBuffer(this.ctx, 2);
    this.explosionBuffer = createNoiseBuffer(this.ctx, 0.5);

    const noiseSource = this.ctx.createBufferSource();
    noiseSource.buffer = this.noiseBuffer;
    noiseSource.loop = true;

    const preFilter = this.ctx.createBiquadFilter();
    preFilter.type = 'highpass';
    preFilter.frequency.value = 80;

    const bodyFilter = this.ctx.createBiquadFilter();
    bodyFilter.type = 'lowpass';
    bodyFilter.frequency.value = 230;
    bodyFilter.Q.value = 1.2;

    const formantA = this.ctx.createBiquadFilter();
    formantA.type = 'bandpass';
    formantA.frequency.value = 210;
    formantA.Q.value = 2.2;

    const formantB = this.ctx.createBiquadFilter();
    formantB.type = 'bandpass';
    formantB.frequency.value = 430;
    formantB.Q.value = 2.8;

    const toneOsc = this.ctx.createOscillator();
    toneOsc.type = 'triangle';
    toneOsc.frequency.value = 55;

    const toneGain = this.ctx.createGain();
    toneGain.gain.value = 0.006;

    const gain = this.ctx.createGain();
    gain.gain.value = 0.00001;

    noiseSource.connect(preFilter);
    preFilter.connect(bodyFilter);
    preFilter.connect(formantA);
    preFilter.connect(formantB);
    bodyFilter.connect(gain);
    formantA.connect(gain);
    formantB.connect(gain);
    toneOsc.connect(toneGain);
    toneGain.connect(gain);
    gain.connect(this.master);

    noiseSource.start();
    toneOsc.start();

    this.thrustBodyFilter = bodyFilter;
    this.thrustFormantA = formantA;
    this.thrustFormantB = formantB;
    this.thrustToneOsc = toneOsc;
    this.thrustGain = gain;
  }

  setThruster(active) {
    if (!this.ctx || !this.thrustGain || !this.thrustBodyFilter || !this.thrustFormantA || !this.thrustFormantB || !this.thrustToneOsc) return;
    const now = this.ctx.currentTime;
    this.thrustGain.gain.cancelScheduledValues(now);
    this.thrustBodyFilter.frequency.cancelScheduledValues(now);
    this.thrustFormantA.frequency.cancelScheduledValues(now);
    this.thrustFormantB.frequency.cancelScheduledValues(now);
    this.thrustToneOsc.frequency.cancelScheduledValues(now);

    if (active) {
      this.thrustBodyFilter.frequency.setTargetAtTime(310, now, 0.05);
      this.thrustFormantA.frequency.setTargetAtTime(255, now, 0.05);
      this.thrustFormantB.frequency.setTargetAtTime(490, now, 0.05);
      this.thrustToneOsc.frequency.setTargetAtTime(63, now, 0.05);
      this.thrustGain.gain.setTargetAtTime(0.05, now, 0.04);
    } else {
      this.thrustBodyFilter.frequency.setTargetAtTime(160, now, 0.08);
      this.thrustFormantA.frequency.setTargetAtTime(180, now, 0.08);
      this.thrustFormantB.frequency.setTargetAtTime(380, now, 0.08);
      this.thrustToneOsc.frequency.setTargetAtTime(46, now, 0.08);
      this.thrustGain.gain.setTargetAtTime(0.00001, now, 0.06);
    }
  }

  playNoiseBurst({
    when,
    duration,
    playbackRate,
    peak,
    filterType,
    frequency,
    q,
    decay = 0.22
  }) {
    const source = this.ctx.createBufferSource();
    source.buffer = this.explosionBuffer;
    source.playbackRate.value = playbackRate;

    const filter = this.ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(Math.max(peak, 0.0001), when);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + decay);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);

    source.addEventListener('ended', () => {
      source.disconnect();
      filter.disconnect();
      gain.disconnect();
    }, { once: true });

    source.start(when);
    source.stop(when + duration);
    return source;
  }

  playAsteroidExplosion({ radius = 20, distance = 0 }) {
    if (!this.ctx || !this.explosionBuffer) return;
    if (this.activeExplosions >= 8) return;

    const distanceMix = clamp(1 - (distance / 1400), 0, 1);
    if (distanceMix <= 0) return;

    const now = this.ctx.currentTime;
    const sizeMix = clamp(radius / 80, 0.3, 1.4);
    const peak = clamp(0.05 + sizeMix * 0.055, 0.05, 0.11) * distanceMix;
    // Two crack accents for r>=40 (incl. largest). Three on the biggest rocks read as a second “hit”
    // next to the body + optional r>=45 layer; logs showed one `playAsteroidExplosion`, not dedupe failure.
    const crackCount = radius >= 40 ? 2 : 1;

    const body = this.playNoiseBurst({
      when: now,
      duration: 0.52,
      playbackRate: clamp(0.78 - radius / 150, 0.2, 0.78),
      peak,
      filterType: 'bandpass',
      frequency: clamp(220 - Math.min(radius, 110) * 1.15, 70, 210),
      q: 0.95,
      decay: 0.44
    });

    this.playNoiseBurst({
      when: now + 0.012,
      duration: 0.38,
      playbackRate: clamp(0.58 - radius / 260, 0.18, 0.62),
      peak: peak * 0.7,
      filterType: 'lowpass',
      frequency: clamp(150 - Math.min(radius, 110) * 0.7, 50, 145),
      q: 0.75,
      decay: 0.38
    });

    if (radius >= 45) {
      this.playNoiseBurst({
        when: now + 0.035,
        duration: 0.62,
        playbackRate: clamp(0.42 - radius / 320, 0.14, 0.42),
        peak: peak * 0.62,
        filterType: 'lowpass',
        frequency: clamp(105 - Math.min(radius, 110) * 0.45, 40, 100),
        q: 0.9,
        decay: 0.56
      });
    }

    const crackOffsets = crackCount === 1 ? [0.03] : [0.03, 0.125];
    const crackPeaks = crackCount === 1 ? [0.72] : [0.46, 0.9];

    crackOffsets.forEach((offset, i) => {
      const isPow = i === 1 || (crackCount === 1 && i === 0);
      this.playNoiseBurst({
        when: now + offset,
        duration: isPow ? 0.14 : 0.11,
        playbackRate: isPow ? 0.9 : 1.12 + i * 0.05,
        peak: peak * crackPeaks[i],
        filterType: isPow ? 'bandpass' : 'highpass',
        frequency: isPow ? 420 : 760 + i * 120,
        q: isPow ? 0.85 : 0.62,
        decay: isPow ? 0.12 : 0.085
      });
    });

    this.activeExplosions += 1;
    body.addEventListener('ended', () => {
      this.activeExplosions = Math.max(0, this.activeExplosions - 1);
    }, { once: true });
  }

  playShieldHit() {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    const duration = 0.45;
    const end = now + duration;

    const oscA = this.ctx.createOscillator();
    const oscB = this.ctx.createOscillator();
    oscA.type = 'square';
    oscB.type = 'square';
    oscA.frequency.value = 132;
    oscB.frequency.value = 139;

    const bp = this.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 13;

    const chopper = this.ctx.createGain();
    chopper.gain.value = 0.5;

    const lfo = this.ctx.createOscillator();
    lfo.type = 'square';
    lfo.frequency.value = 32;
    const lfoDepth = this.ctx.createGain();
    lfoDepth.gain.value = 0.35;
    lfo.connect(lfoDepth);
    lfoDepth.connect(chopper.gain);

    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(0.08, now + 0.008);
    env.gain.setValueAtTime(0.08, now + 0.05);
    env.gain.exponentialRampToValueAtTime(0.0001, end);

    oscA.connect(bp);
    oscB.connect(bp);
    bp.connect(chopper);
    chopper.connect(env);
    env.connect(this.master);

    oscA.start(now);
    oscB.start(now);
    lfo.start(now);
    oscA.stop(end + 0.02);
    oscB.stop(end + 0.02);
    lfo.stop(end + 0.02);

    oscA.addEventListener('ended', () => {
      oscA.disconnect();
      oscB.disconnect();
      bp.disconnect();
      chopper.disconnect();
      env.disconnect();
      lfo.disconnect();
      lfoDepth.disconnect();
    }, { once: true });
  }

  playDeathThunder() {
    if (!this.ctx || !this.explosionBuffer || !this.master) return;

    const now = this.ctx.currentTime;

    this.playNoiseBurst({
      when: now,
      duration: 0.14,
      playbackRate: 0.55,
      peak: 0.12,
      filterType: 'bandpass',
      frequency: 520,
      q: 1.1,
      decay: 0.12
    });

    this.playNoiseBurst({
      when: now + 0.02,
      duration: 1.2,
      playbackRate: 0.18,
      peak: 0.095,
      filterType: 'lowpass',
      frequency: 120,
      q: 0.85,
      decay: 0.95
    });

    this.playNoiseBurst({
      when: now + 0.09,
      duration: 0.75,
      playbackRate: 0.24,
      peak: 0.06,
      filterType: 'bandpass',
      frequency: 190,
      q: 0.75,
      decay: 0.62
    });

    const osc = this.ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(64, now);
    osc.frequency.exponentialRampToValueAtTime(36, now + 0.22);
    osc.frequency.exponentialRampToValueAtTime(24, now + 0.95);

    const oscFilter = this.ctx.createBiquadFilter();
    oscFilter.type = 'lowpass';
    oscFilter.frequency.value = 140;
    oscFilter.Q.value = 0.7;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.042, now + 0.035);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.0);

    osc.connect(oscFilter);
    oscFilter.connect(gain);
    gain.connect(this.master);

    osc.addEventListener('ended', () => {
      osc.disconnect();
      oscFilter.disconnect();
      gain.disconnect();
    }, { once: true });

    osc.start(now);
    osc.stop(now + 1.02);
  }

  playBulletShot() {
    if (!this.ctx || !this.master) return;

    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(261.63, now);
    osc.frequency.exponentialRampToValueAtTime(65.41, now + 0.045);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.1, now + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.042);

    osc.connect(gain);
    gain.connect(this.master);

    osc.addEventListener('ended', () => {
      osc.disconnect();
      gain.disconnect();
    }, { once: true });

    osc.start(now);
    osc.stop(now + 0.048);
  }

  playTrumpetNote({ when, frequency, duration, peak = 0.04, sustain = false }) {
    if (!this.ctx || !this.master) return;

    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sawtooth';
    osc1.frequency.setValueAtTime(frequency, when);
    osc1.detune.setValueAtTime(-4, when);

    const osc2 = this.ctx.createOscillator();
    osc2.type = 'square';
    osc2.frequency.setValueAtTime(frequency, when);
    osc2.detune.setValueAtTime(5, when);

    const formant = this.ctx.createBiquadFilter();
    formant.type = 'bandpass';
    formant.frequency.value = clamp(frequency * 3, 800, 2400);
    formant.Q.value = 1.4;

    const tame = this.ctx.createBiquadFilter();
    tame.type = 'lowpass';
    tame.frequency.value = 4800;
    tame.Q.value = 0.7;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(peak, when + 0.022);
    if (sustain) {
      gain.gain.setValueAtTime(peak * 0.92, when + duration * 0.55);
      gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    } else {
      gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    }

    osc1.connect(formant);
    osc2.connect(formant);
    formant.connect(tame);
    tame.connect(gain);
    gain.connect(this.master);

    const stopAt = when + duration + 0.04;
    osc1.addEventListener('ended', () => {
      osc1.disconnect();
      osc2.disconnect();
      formant.disconnect();
      tame.disconnect();
      gain.disconnect();
    }, { once: true });

    osc1.start(when);
    osc2.start(when);
    osc1.stop(stopAt);
    osc2.stop(stopAt);
  }

  playCtfScoreFanfare() {
    if (!this.ctx || !this.master) return;

    const now = this.ctx.currentTime + 0.02;
    const eighth = 0.2;
    const quarterDur = 0.32;
    const eighthDur = 0.16;
    const dottedHalf = 1.25;

    const chord = [523.25, 659.25, 783.99];
    const slots = [
      { start: 0, duration: quarterDur, sustain: false, peak: 0.045 },
      { start: 3 * eighth, duration: eighthDur, sustain: false, peak: 0.045 },
      { start: 4 * eighth, duration: dottedHalf, sustain: true, peak: 0.05 }
    ];

    slots.forEach((slot) => {
      chord.forEach((frequency) => {
        this.playTrumpetNote({
          when: now + slot.start,
          frequency,
          duration: slot.duration,
          peak: slot.peak,
          sustain: slot.sustain
        });
      });
    });
  }

  playLobbyBellChime(gainMul = 1) {
    if (!this.ctx || !this.master) return;
    const now = this.ctx.currentTime;
    const out = this.ctx.createGain();
    out.gain.value = clamp(gainMul, 0.35, 2.5);
    out.connect(this.master);
    const d = [out];
    const f1 = 784;
    const f2 = 1174.66;
    const gap = 0.016;
    const p1 = 0.075;
    const p2 = 0.055;
    const dur = 0.22;
    const atk = 0.02;
    const sineChime = (f, t0, peak, duration, attack) => {
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, t0);
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(peak, t0 + attack);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
      osc.connect(g);
      g.connect(out);
      const stopAt = t0 + duration + 0.04;
      osc.start(t0);
      osc.stop(stopAt);
      d.push(osc, g);
      return { osc, stopAt };
    };
    const partA = sineChime(f1, now, p1, dur, atk);
    const partB = sineChime(f2, now + gap, p2, dur * 0.92, atk * 0.95);
    const tailOsc = partA.stopAt >= partB.stopAt ? partA.osc : partB.osc;
    tailOsc.addEventListener('ended', () => {
      for (const n of d) {
        try { n.disconnect(); } catch {}
      }
    }, { once: true });
  }

  playLobbyPlayerJoinedBell() {
    this.playLobbyBellChime(1);
  }

  playLobbyRingBell() {
    this.playLobbyBellChime(1.5);
  }

  playWallBoing() {
    if (!this.ctx || !this.master) return;

    const now = this.ctx.currentTime;
    const f0 = 92;
    const hold = 0.12;
    const fEnd = 38;
    const sagT = 0.48;
    const lpF = 240;
    const peak = 0.078;
    const atk = 0.032;
    const stop = 0.68;

    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(f0, now);
    osc.frequency.setValueAtTime(f0, now + hold);
    osc.frequency.exponentialRampToValueAtTime(fEnd, now + hold + sagT);
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = lpF;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(peak, now + atk);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + stop * 0.88);
    osc.connect(lp);
    lp.connect(gain);
    gain.connect(this.master);

    const stopAt = now + stop;
    const cleanup = () => {
      try {
        osc.disconnect();
        lp.disconnect();
        gain.disconnect();
      } catch {}
    };
    osc.addEventListener('ended', cleanup, { once: true });
    osc.start(now);
    osc.stop(stopAt);
  }

  playStartChime() {
    if (!this.ctx || !this.master) return;

    const beat = 0.22;
    const now = this.ctx.currentTime + 0.02;
    const notes = [
      { frequency: 261.63, start: 0, duration: beat * 0.78 },
      { frequency: 329.63, start: beat, duration: beat * 0.78 },
      { frequency: 392.0, start: beat * 2, duration: beat * 0.78 },
      { frequency: 523.25, start: beat * 3, duration: beat * 1.75 }
    ];

    notes.forEach((note) => {
      const isLast = note === notes.at(-1)
      this.playTone({
        when: now + note.start,
        frequency: note.frequency,
        duration: note.duration,
        type: isLast ? 'sine' : 'triangle',
        peak: isLast ? 0.05 : 0.04
      });
    });
  }

  destroy() {
    this.destroyed = true;
    this.setThruster(false);
    if (!this.ctx) return;
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.thrustGain = null;
    this.thrustBodyFilter = null;
    this.thrustFormantA = null;
    this.thrustFormantB = null;
    this.thrustToneOsc = null;
    this.noiseBuffer = null;
    this.explosionBuffer = null;
    this.activeExplosions = 0;
    ctx.close().catch(() => {});
  }
}
